import React, { useState } from 'react';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { getStatusColor } from '../../data/helpers';
import { generateCertificate } from '../../utils/certificateUtils';

const { FiBookOpen, FiCalendar, FiChevronDown, FiChevronUp, FiCheckCircle, FiXCircle, FiMinusCircle, FiClock, FiDownload } = FiIcons;

const formatTime = (seconds) => {
  if (!seconds) return '0m';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m`;
  return `${s}s`;
};

const TrainingSection = ({ assignedCampaigns = [], learnerName = 'Learner' }) => {
  const [expanded, setExpanded] = useState({});

  const toggleExpand = (id) => {
    setExpanded(prev => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
      <h3 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
        <SafeIcon icon={FiBookOpen} className="text-blue-500" /> Assigned Training
      </h3>
      {assignedCampaigns.length === 0 ? (
        <p className="text-sm text-slate-400">No training campaigns assigned.</p>
      ) : (
        <div className="space-y-3">
          {assignedCampaigns.map(a => (
            <div key={a.id} className="p-3 rounded-lg border border-slate-100 bg-slate-50 hover:bg-slate-100 transition-colors">
              <div 
                className="flex items-start justify-between gap-2 mb-2 cursor-pointer"
                onClick={() => toggleExpand(a.id)}
              >
                <div>
                  <p className="text-sm font-medium text-slate-800 flex items-center gap-2 hover:text-blue-600 transition-colors">
                    {a.campaignName}
                    <SafeIcon icon={expanded[a.id] ? FiChevronUp : FiChevronDown} className="text-slate-400" />
                  </p>
                  <p className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                    <SafeIcon icon={FiCalendar} /> {a.startDate || 'N/A'} → {a.endDate || 'N/A'}
                  </p>
                </div>
                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold border flex-shrink-0 ${getStatusColor(a.status)}`}>{a.status}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex-1 bg-slate-200 rounded-full h-1.5">
                  <div className={`h-1.5 rounded-full ${a.pct === 100 ? 'bg-emerald-500' : a.pct >= 50 ? 'bg-blue-500' : 'bg-slate-400'}`} style={{ width: `${a.pct}%` }} />
                </div>
                <span className="text-xs font-semibold text-slate-600 w-8 text-right">{a.pct}%</span>
              </div>

              {expanded[a.id] && a.modules && a.modules.length > 0 && (
                <div className="mt-4 pt-3 border-t border-slate-200 space-y-2">
                  <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Modules & Scores</p>
                  {a.modules.map(m => {
                    const isCompleted = m.status === 'Completed';
                    const isExpired = m.isExpired;
                    const isValidCompletion = isCompleted && !isExpired;
                    
                    const score = m.score != null ? m.score : null;
                    const passed = score != null ? score >= 80 : false;
                    const attempts = m.attempts || 0;
                    const timeSpent = m.time_spent || 0;

                    let StatusIcon = FiMinusCircle;
                    let iconColor = 'text-slate-400';
                    if (isCompleted) {
                       if (isExpired) {
                         StatusIcon = FiClock;
                         iconColor = 'text-orange-500';
                       } else {
                         StatusIcon = passed ? FiCheckCircle : FiXCircle;
                         iconColor = passed ? 'text-emerald-500' : 'text-red-500';
                       }
                    }

                    return (
                      <div key={m.id} className="flex items-center justify-between bg-white p-2.5 rounded-lg border border-slate-200 text-sm shadow-sm">
                        <div className="flex items-center gap-2.5 truncate pr-2">
                          <SafeIcon icon={StatusIcon} className={`text-base ${iconColor}`} />
                          <div className="flex flex-col truncate">
                            <span className="text-slate-700 font-medium truncate">{m.title}</span>
                            <div className="flex items-center gap-2 mt-0.5">
                              {attempts > 0 && (
                                <span className="text-[10px] text-slate-400 font-medium">Attempts: {attempts}</span>
                              )}
                              {timeSpent > 0 && (
                                <span className="text-[10px] text-slate-400 font-medium flex items-center gap-1">
                                  <SafeIcon icon={FiClock} className="text-[10px]" /> {formatTime(timeSpent)}
                                </span>
                              )}
                              {m.expires_at && (
                                <span className={`text-[10px] font-medium ${isExpired ? 'text-orange-500' : 'text-slate-400'}`}>
                                  {isExpired ? 'Expired: ' : 'Expires: '} {new Date(m.expires_at).toLocaleDateString()}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 flex-shrink-0">
                          {isValidCompletion && passed && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                generateCertificate(learnerName, m.title, new Date().toLocaleDateString());
                              }}
                              className="text-blue-600 hover:bg-blue-50 p-1.5 rounded-lg transition-colors flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider"
                              title="Download Certificate"
                            >
                              <SafeIcon icon={FiDownload} className="text-sm" />
                            </button>
                          )}
                          <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border ${isValidCompletion ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : isExpired ? 'bg-orange-50 border-orange-200 text-orange-700' : 'bg-slate-50 border-slate-200 text-slate-500'}`}>
                            {isExpired ? 'Expired' : m.status}
                          </span>
                          {score != null ? (
                            <div className="flex flex-col items-end min-w-[48px]">
                              <span className={`text-sm font-bold ${passed ? 'text-emerald-600' : 'text-red-600'}`}>
                                {score}%
                              </span>
                              <span className={`text-[9px] font-bold uppercase tracking-wider ${passed ? 'text-emerald-500' : 'text-red-500'}`}>
                                {passed ? 'Pass' : 'Fail'}
                              </span>
                            </div>
                          ) : (
                            <span className="text-xs text-slate-400 min-w-[48px] text-right font-medium">-</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TrainingSection;